/**
 * GTM / GA4 dataLayer helper (account app).
 *
 * Зеркало mall'овского src/ZyMall/utils/gtm.js - единая таксономия событий на
 * оба приложения (mall + account). Контейнер GTM подключается в header.volt.
 *
 * Правила таксономии:
 *   - Стандартные GA4 ecommerce (view_item_list / view_item / select_item /
 *     begin_checkout / purchase) - где есть аналог.
 *   - purchase шлётся ТОЛЬКО за реальные деньги (Stripe $).
 *   - Подписка Pass -> subscribe / subscription_upgraded (вне ecommerce-revenue).
 *   - Кастом - под pass-gate / кредиты / кабинет.
 */

export const GTM_EVENTS = {

    // ── ① Вход ──────────────────────────────────────────────
    SIGN_IN_REQUESTED : 'sign_in_requested',   // запрос кода / magic-link
    SIGN_UP           : 'sign_up',             // успешная регистрация (key event)
    LOGIN             : 'login',               // успешный вход

    // ── Ecommerce-воронка (стандартные GA4) ─────────────────
    VIEW_ITEM_LIST   : 'view_item_list',
    VIEW_ITEM        : 'view_item',
    SELECT_ITEM      : 'select_item',
    BEGIN_CHECKOUT   : 'begin_checkout',
    ADD_PAYMENT_INFO : 'add_payment_info',
    PURCHASE         : 'purchase',

    // ── ② Лендинг ───────────────────────────────────────────
    GENERATE_LEAD    : 'generate_lead',

    // ── ③ Каталог · покупка продукта ────────────────────────
    PRODUCT_CTA_VIEWED      : 'product_cta_viewed',
    PASS_PROMO_CLICKED      : 'pass_promo_clicked',
    CREDITS_UNLOCK_STARTED  : 'credits_unlock_started',
    PASS_UPGRADE_CLICKED    : 'pass_upgrade_clicked',
    PASS_GATE_CTA_CLICKED   : 'pass_gate_cta_clicked',

    // ── ④ Подписка (Pass) ───────────────────────────────────
    PRICING_VIEWED        : 'pricing_viewed',          // source: gate|product|account
    SUBSCRIPTION_UPGRADED : 'subscription_upgraded',   // апгрейд плана (prorated, key event)

    // ── ⑤ Личный кабинет ────────────────────────────────────
    ACCOUNT_CTA_CLICKED : 'account_cta_clicked',   // action: browse_catalog|get_tokens
    TOKEN_TOPUP_STARTED : 'token_topup_started',   // открытие confirm-модалки top-up

    // ── Кастомные конверсии / воронка ───────────────────────
    SUBSCRIBE                : 'subscribe',
    CREDITS_UNLOCK_COMPLETED : 'credits_unlock_completed',
    PASS_GATE_VIEWED         : 'pass_gate_viewed',
    PASS_GATE_BLOCKED        : 'pass_gate_blocked',
};

/**
 * Пуш события в dataLayer. Для ecommerce (payload содержит `ecommerce`)
 * перед push'ем шлём `{ ecommerce: null }` - GA4 требует сброс.
 *
 * @param {string} event   имя события (GTM_EVENTS.*)
 * @param {object} payload данные; для ecommerce - { ecommerce: {...} }
 */
export function track( event, payload = {} ) {

    if( ! event )
        return;

    window.dataLayer = window.dataLayer || [];

    if( Object.prototype.hasOwnProperty.call( payload, 'ecommerce' ) )
        window.dataLayer.push({ ecommerce: null });

    window.dataLayer.push({ event, ...payload });
}

/**
 * Хелпер для ecommerce-событий.
 *
 * ВАЖНО (WP2): в `ecommerce` - ТОЛЬКО стандартные GA4-поля (currency, value,
 * items, transaction_id, item_list_*). Кастомные (plan_name, billing_period,
 * source, payment_type, ...) - ТРЕТЬИМ аргументом, на верхний уровень события
 * (иначе top-level DLV их не подхватят - F-09/F-14/F-16).
 *
 * @param {string} event  имя ecommerce-события
 * @param {object} data   стандартные ecommerce-поля -> { ecommerce: data }
 * @param {object} params кастомные поля -> верхний уровень события
 */
export function trackEcommerce( event, data = {}, params = {} ) {
    track( event, { ...params, ecommerce: data } );
}

/**
 * Единый билдер ecommerce-айтема (WP1) - канон shape для ВСЕХ воронок,
 * чтобы GA4 сшивал товар по стадиям. Зеркало mall'овского gtmItem.
 *
 * Правила:
 *   - item_id  - ВСЕГДА строка (slug), не числовой DB id;
 *   - item_name - title, не slug;
 *   - item_category - ТОЛЬКО дискриминатор: product | tokens | subscription | bundle;
 *   - price/discount - числа в $.
 *
 * @param {object} p { id, name, category, price, quantity?, discount?, index? }
 * @returns {object} ecommerce item
 */
export function gtmItem( p = {} ) {

    const item = {
        item_id       : p.id != null ? String( p.id ) : '',
        item_name     : p.name || '',
        item_category : p.category || 'product',
        price         : p.price != null ? +( +p.price ).toFixed( 2 ) : 0,
        quantity      : p.quantity != null ? p.quantity : 1,
    };

    if( p.discount != null )
        item.discount = +( +p.discount ).toFixed( 2 );

    if( p.index != null )
        item.index = p.index;

    return item;
}

/**
 * Единый билдер кастомных product-параметров (top-level). Централизует тип:
 * product_id ВСЕГДА строка (F-03). Зеркало mall'овского productParams.
 *
 * @param {object} p { id, type?, ...extra }
 * @returns {object} { product_id, product_type?, ...extra }
 */
export function productParams( p = {} ) {

    const { id, type, ...extra } = p;

    const out = { ...extra };

    if( id != null )
        out.product_id = String( id );

    if( type )
        out.product_type = type;

    return out;
}
